import { SubSink } from 'subsink';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ApmTranslatePipe, ResourcesService } from '@mitsubishi/core';
import { NewsService, NewsTagContextEnum } from '@mitsubishi/news/data-access';
import { ButtonComponent, MomentDatePipe } from '@mitsubishi/shared/ui';
import { WindowedSpinnerComponent } from '@mitsubishi/ui/spinner';

import { tapResponse } from '@ngrx/operators';

import { NewsImgComponent } from '../news-img/news-img.component';

export type NewsDialogData = {
  newsId: string;
  projectId?: string;
  selectionId?: string;
};

@Component({
  selector: 'apm-news-detail-dialog',
  templateUrl: './news-detail-dialog.component.html',
  styleUrls: ['./news-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ButtonComponent,
    WindowedSpinnerComponent,
    ApmTranslatePipe,
    MomentDatePipe,
    NewsImgComponent,
  ],
})
export class NewsDetailDialogComponent implements OnInit {
  #newsService = inject(NewsService);
  #resourcesService = inject(ResourcesService);

  isLoading = signal(true);
  hasError = signal(false);
  tags = signal<string[]>([]);

  private subs = new SubSink();

  newsDetails$ = this.#newsService.getNewsDetail(this.dialogData.newsId).pipe(
    tapResponse({
      next: news => {
        const normalizedTags = news.tags
          .filter(tag => !!tag.value)
          .map(tag => {
            return tag.context === NewsTagContextEnum.Class || tag.context === NewsTagContextEnum.Subclass
              ? this.#resourcesService.getTextResource(tag.value)
              : tag.value;
          });
        this.tags.set(normalizedTags);
      },
      error: () => this.hasError.set(true),
      finalize: () => {
        this.setLoading = false;
      },
    })
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: NewsDialogData,
    public dialogRef: MatDialogRef<NewsDetailDialogComponent>
  ) {}

  ngOnInit(): void {
    this.setNewsAsRead();
  }

  set setLoading(value: boolean) {
    this.isLoading.set(value);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  setNewsAsRead(): void {
    if (!this.dialogData.projectId || !this.dialogData.selectionId) return;

    this.subs.sink = this.#newsService
      .setNewsAsRead(this.dialogData.projectId, this.dialogData.selectionId, this.dialogData.newsId)
      .subscribe();
  }
}
